import { useEffect, useRef, useState } from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import {
  Activity,
  ArrowLeft,
  ChevronDown,
  Clock,
  Columns3,
  Copy,
  ExternalLink,
  Heart,
  Languages,
  LayoutTemplate,
  Maximize2,
  MessageSquare,
  Repeat2,
  Sparkles,
  Star,
  Tag,
  User,
  X
} from 'lucide-react'
import { api } from '../lib/api'
import type { Actor, BoardColumn, Post, Project, ProjectMember, ReplyTemplate } from '../lib/types'
import { formatCount, formatDateTime } from '../lib/format'
import { Link, navigate } from '../lib/router'
import { errorText, useToast } from '../components/Toaster'
import { useConfirm } from '../components/Confirm'
import { RichEditor } from '../components/RichEditor'
import { Comments } from '../components/Comments'
import { Avatar, Button, IconButton, Notice, SaveStatus, Select, Spinner, Textarea } from '../ds'

type SaveState = 'idle' | 'saving' | 'saved' | 'error'

function htmlToText(html: string): string {
  const div = document.createElement('div')
  div.innerHTML = html
  return (div.textContent || '').replace(/\n{3,}/g, '\n\n').trim()
}

// Простой текст шаблона → HTML для rich-редактора: абзацы по пустой строке,
// переносы внутри абзаца — <br>. Экранируем спецсимволы.
function templateToHtml(text: string): string {
  const escape = (s: string) =>
    s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  return text
    .trim()
    .split(/\n{2,}/)
    .map((block) => `<p>${escape(block).replace(/\n/g, '<br>')}</p>`)
    .join('')
}

export function PostView({
  postId,
  actor,
  variant = 'page',
  onClose,
  onPostChange
}: {
  postId: string
  actor: Actor
  variant?: 'page' | 'peek'
  onClose?: () => void
  // Сообщает родителю об изменении поста, чтобы доска обновила одну карточку
  // точечно (без полной перезагрузки колонок).
  onPostChange?: (post: Post) => void
}) {
  const { notify } = useToast()
  const confirm = useConfirm()
  const [post, setPost] = useState<Post | null>(null)
  const [project, setProject] = useState<Project | null>(null)
  const [members, setMembers] = useState<ProjectMember[]>([])
  const [boardColumns, setBoardColumns] = useState<BoardColumn[]>([])
  const [templates, setTemplates] = useState<ReplyTemplate[]>([])
  const [keywordName, setKeywordName] = useState('')
  const [loadError, setLoadError] = useState('')

  const [draftHtml, setDraftHtml] = useState('')
  const [rejectReason, setRejectReason] = useState('')
  const [generating, setGenerating] = useState(false)
  const [saveState, setSaveState] = useState<SaveState>('idle')
  // Когда жест (клик-вне / Escape) пришёлся на момент, где поверх peek был открыт любой
  // вложенный Radix-оверлей (селект «Статус»/«Исполнитель», меню шаблонов, поповер) — этот
  // жест гасит ИМЕННО его, а не панель. Немодальный Dialog диспатчит своё закрытие отложенно,
  // уже после исчезновения верхнего слоя, поэтому синхронной проверки в onInteractOutside мало.
  // Ловим начало жеста в capture-фазе (оверлей ещё в DOM) и ставим короткое окно подавления.
  const overlayDismissAtRef = useRef(0)

  // Общий перехват для ВСЕХ вложенных оверлеев (не привязан к конкретному компоненту):
  // на старте любого pointerdown/Escape проверяем, открыт ли Radix-слой поверх панели.
  useEffect(() => {
    if (variant !== 'peek') return
    const overlayOpen = () =>
      !!document.querySelector('[data-radix-popper-content-wrapper], [role="menu"], [role="alertdialog"]')
    const onPointerDown = () => {
      if (overlayOpen()) overlayDismissAtRef.current = Date.now()
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && overlayOpen()) overlayDismissAtRef.current = Date.now()
    }
    document.addEventListener('pointerdown', onPointerDown, true)
    document.addEventListener('keydown', onKeyDown, true)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true)
      document.removeEventListener('keydown', onKeyDown, true)
    }
  }, [variant])

  useEffect(() => {
    let alive = true
    api
      .getPost(postId)
      .then((res) => {
        if (!alive) return
        setPost(res.post)
        setDraftHtml(res.post.draftHtml)
        setRejectReason(res.post.rejectReason)
        const pid = res.post.projectId ?? ''
        return Promise.all([
          api.getProject(pid),
          api.listProjectMembers(pid),
          api.listBoardColumns(pid),
          api.listKeywords(pid),
          api.listReplyTemplates(pid)
        ]).then((loaded) => ({ loaded, keywordId: res.post.keywordId }))
      })
      .then((res) => {
        if (alive && res) {
          const [proj, mem, cols, kw, tpl] = res.loaded
          setProject(proj.project)
          setMembers(mem.members)
          setBoardColumns(cols.columns)
          setKeywordName(kw.keywords.find((k) => k.id === res.keywordId)?.query ?? '')
          setTemplates(tpl.templates)
        }
      })
      .catch((err) => alive && setLoadError(errorText(err)))
    return () => {
      alive = false
    }
  }, [postId])

  async function patch(input: Parameters<typeof api.updatePost>[1], okMessage?: string) {
    try {
      const res = await api.updatePost(postId, input)
      setPost(res.post)
      onPostChange?.(res.post)
      if (okMessage) notify(okMessage)
      return res.post
    } catch (err) {
      notify(errorText(err), 'error')
      return null
    }
  }

  async function generate() {
    setGenerating(true)
    try {
      const res = await api.generateDraft(postId)
      setDraftHtml(res.draftHtml)
      setPost(res.post)
      onPostChange?.(res.post)
      notify('Черновик сгенерирован')
    } catch (err) {
      notify(errorText(err), 'error')
    } finally {
      setGenerating(false)
    }
  }

  async function saveDraft() {
    setSaveState('saving')
    const result = await patch({ draftHtml })
    if (result) {
      setSaveState('saved')
      setTimeout(() => setSaveState('idle'), 2000)
    } else {
      setSaveState('error')
    }
  }

  async function copyDraft() {
    const text = htmlToText(draftHtml)
    if (!text) {
      notify('Черновик пуст', 'error')
      return
    }
    try {
      await navigator.clipboard.writeText(text)
      notify('Скопировано — вставьте в Threads')
    } catch {
      notify('Не удалось скопировать', 'error')
    }
  }

  // Подставляем шаблон в редактор черновика. Если черновик не пуст — подтверждаем замену,
  // чтобы не потерять написанное. Дальше текст редактируется на лету перед копированием.
  async function applyTemplate(template: ReplyTemplate) {
    if (htmlToText(draftHtml).trim()) {
      const ok = await confirm({
        title: 'Заменить черновик?',
        message: `Текущий текст черновика будет заменён шаблоном «${template.title}».`,
        confirmLabel: 'Заменить',
        danger: false
      })
      if (!ok) return
    }
    setDraftHtml(templateToHtml(template.body))
    notify('Шаблон подставлен — отредактируйте и скопируйте')
  }

  // Список для назначения: активные участники проекта + текущий исполнитель (если его уже удалили).
  const assigneeEmails = post
    ? (Array.from(
        new Set([
          ...members.filter((member) => member.status === 'active').map((member) => member.email),
          post.assignedToEmail
        ].filter(Boolean))
      ) as string[])
    : []
  const currentColumn = post ? boardColumns.find((column) => column.id === post.columnId) : null
  const showRejectReason = currentColumn?.legacyStatus === 'rejected' || (!currentColumn && post?.status === 'rejected')
  // Человекочитаемый язык поста для строки-свойства.
  const languageLabel = post && post.language && post.language !== 'und' ? post.language.toUpperCase() : '—'
  const metrics = post?.metrics
  const hasMetrics = !!metrics && (metrics.likes != null || metrics.replies != null || metrics.reposts != null)

  // Общее тело поста — одинаково для полной страницы и peek-панели.
  // Раскладка в стиле Notion: заголовок → строки-свойства (иконка+лейбл | значение) → текст поста.
  const body = post ? (
    <>
      <header className="peek-head">
        <Avatar email={`${post.authorUsername}@threads`} size={28} />
        <h1 className="peek-title">@{post.authorUsername}</h1>
        <div className="peek-head-actions">
          <IconButton
            label={post.isFavorite ? 'Убрать из избранного' : 'В избранное'}
            onClick={() => patch({ isFavorite: !post.isFavorite })}
          >
            <Star
              size={16}
              fill={post.isFavorite ? 'currentColor' : 'none'}
              style={{ color: post.isFavorite ? 'var(--amber)' : undefined }}
            />
          </IconButton>
          {post.url && (
            <a className="btn btn-secondary btn-sm" href={post.url} target="_blank" rel="noreferrer">
              <ExternalLink size={14} /> В Threads
            </a>
          )}
        </div>
      </header>

      <div className="prop-list">
        <div className="prop-row">
          <div className="prop-label">
            <Columns3 size={15} />
            <span>Колонка</span>
          </div>
          <div className="prop-value">
            <Select
              className="prop-select"
              value={post.columnId || ''}
              onValueChange={(value) => patch({ columnId: value })}
              options={boardColumns.map((column) => ({ value: column.id, label: column.name }))}
            />
          </div>
        </div>
        <div className="prop-row">
          <div className="prop-label">
            <User size={15} />
            <span>Исполнитель</span>
          </div>
          <div className="prop-value">
            <Select
              className="prop-select"
              placeholder="Пусто"
              value={post.assignedToEmail || ''}
              onValueChange={(value) =>
                patch({ assignedToEmail: value || null }, value ? 'Исполнитель назначен' : 'Исполнитель снят')
              }
              options={[
                { value: '', label: 'Не назначен' },
                ...assigneeEmails.map((email) => ({
                  value: email,
                  label: email === actor.email ? `${email} (вы)` : email
                }))
              ]}
            />
          </div>
        </div>
        <div className="prop-row">
          <div className="prop-label">
            <Tag size={15} />
            <span>Ключевик</span>
          </div>
          <div className="prop-value">
            {keywordName ? <span className="prop-tag">{keywordName}</span> : <span className="prop-empty">Пусто</span>}
          </div>
        </div>
        <div className="prop-row">
          <div className="prop-label">
            <Languages size={15} />
            <span>Язык</span>
          </div>
          <div className="prop-value">{languageLabel}</div>
        </div>
        <div className="prop-row">
          <div className="prop-label">
            <Clock size={15} />
            <span>{post.publishedAt ? 'Опубликован' : 'Найден'}</span>
          </div>
          <div className="prop-value">{formatDateTime(post.publishedAt || post.foundAt)}</div>
        </div>
        {hasMetrics && (
          <div className="prop-row">
            <div className="prop-label">
              <Activity size={15} />
              <span>Вовлечённость</span>
            </div>
            <div className="prop-value prop-metrics">
              <span>
                <Heart size={13} /> {formatCount(metrics?.likes ?? 0)}
              </span>
              <span>
                <MessageSquare size={13} /> {formatCount(metrics?.replies ?? 0)}
              </span>
              <span>
                <Repeat2 size={13} /> {formatCount(metrics?.reposts ?? 0)}
              </span>
            </div>
          </div>
        )}
        {showRejectReason && (
          <div className="prop-row prop-row-block">
            <div className="prop-label">
              <X size={15} />
              <span>Причина отклонения</span>
            </div>
            <div className="prop-value">
              <Textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                onBlur={() => {
                  if (rejectReason.trim() !== post.rejectReason) {
                    patch({ rejectReason: rejectReason.trim() })
                  }
                }}
                placeholder="Почему пост не подходит…"
              />
            </div>
          </div>
        )}
      </div>

      <article className="post-source">
        <div className="post-source-text">{post.text || '(пост без текста)'}</div>
      </article>

      <section className="draft">
        <div className="draft-head">
          <div className="row" style={{ gap: 8 }}>
            <span className="section-label">Черновик комментария</span>
            <SaveStatus state={saveState} />
          </div>
          <div className="row" style={{ gap: 8 }}>
            <DropdownMenu.Root>
              <DropdownMenu.Trigger className="btn btn-secondary btn-sm">
                <LayoutTemplate size={14} /> Шаблоны <ChevronDown size={14} />
              </DropdownMenu.Trigger>
              <DropdownMenu.Portal>
                <DropdownMenu.Content className="menu tpl-menu" align="end" sideOffset={6}>
                  {templates.length === 0 ? (
                    <div className="tpl-menu-empty">
                      Нет шаблонов. Добавьте их в настройках проекта → «Шаблоны ответов».
                    </div>
                  ) : (
                    templates.map((t) => (
                      <DropdownMenu.Item
                        key={t.id}
                        className="menu-item tpl-menu-item"
                        onSelect={() => applyTemplate(t)}
                      >
                        <span className="tpl-menu-title">{t.title}</span>
                        <span className="tpl-menu-body">{t.body}</span>
                      </DropdownMenu.Item>
                    ))
                  )}
                </DropdownMenu.Content>
              </DropdownMenu.Portal>
            </DropdownMenu.Root>
            <Button variant="secondary" size="sm" onClick={generate} disabled={generating}>
              <Sparkles size={14} /> {generating ? 'Генерируем…' : 'Сгенерировать ИИ'}
            </Button>
          </div>
        </div>

        <RichEditor value={draftHtml} onChange={setDraftHtml} />

        <div className="draft-actions">
          <Button onClick={saveDraft} disabled={saveState === 'saving'}>
            Сохранить черновик
          </Button>
          <Button variant="secondary" onClick={copyDraft}>
            <Copy size={14} /> Копировать текст
          </Button>
        </div>
      </section>

      {post.projectId && (
        <Comments
          postId={postId}
          projectId={post.projectId}
          actor={actor}
          members={members}
          canModerate={Boolean(project?.canManageProject)}
        />
      )}
    </>
  ) : null

  // --- Peek-панель (выезжает справа, как в Notion) ---
  // Немодальный Radix Dialog: даёт Escape + клик-снаружи и не блокирует доску
  // (modal=false → нет focus-trap и scroll-lock, фон остаётся интерактивным).
  // Монтируется только когда показана, поэтому всегда open; закрытие → onClose.
  if (variant === 'peek') {
    const title = post ? `@${post.authorUsername}` : 'Пост'
    return (
      <Dialog.Root
        open
        modal={false}
        onOpenChange={(nextOpen) => {
          if (nextOpen) return
          // Не закрываем панель, если этот жест только что гасил вложенный оверлей
          // (см. overlayDismissAtRef выше) — закрытие диалога приходит отложенно.
          if (Date.now() - overlayDismissAtRef.current < 350) return
          onClose?.()
        }}
      >
        <Dialog.Portal>
          <Dialog.Content
            className="peek"
            aria-label={title}
            aria-describedby={undefined}
            // Панель — для чтения: не угоняем фокус ни при открытии, ни при закрытии.
            onOpenAutoFocus={(event) => event.preventDefault()}
            onCloseAutoFocus={(event) => event.preventDefault()}
            // Уход фокуса не закрывает панель (читалка) — закрытие только кликом-вне или Escape.
            onFocusOutside={(event) => event.preventDefault()}
            // Клик по карточке доски не закрывает панель — она сама переключит peek на другой пост.
            onInteractOutside={(event) => {
              const target = event.detail.originalEvent.target as HTMLElement | null
              if (target?.closest('.post-card')) event.preventDefault()
            }}
          >
            <Dialog.Title className="sr-only">{title}</Dialog.Title>
            <div className="peek-bar">
              <IconButton label="Закрыть" onClick={() => onClose?.()}>
                <X size={16} />
              </IconButton>
              <IconButton label="Открыть страницей" onClick={() => navigate(`/posts/${postId}`, {})}>
                <Maximize2 size={15} />
              </IconButton>
            </div>
            <div className="peek-body">
              {loadError ? (
                <Notice>{loadError}</Notice>
              ) : !post ? (
                <div className="peek-loading">
                  <Spinner />
                </div>
              ) : (
                body
              )}
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    )
  }

  // --- Полная страница ---
  if (loadError) {
    return (
      <div className="page page-narrow">
        <Link href="/" className="back-link">
          <ArrowLeft size={14} /> К проектам
        </Link>
        <Notice>{loadError}</Notice>
      </div>
    )
  }

  if (!post) {
    return (
      <div className="loading-screen">
        <Spinner />
      </div>
    )
  }

  return (
    <div className="page page-narrow">
      <Link href={`/projects/${post.projectId}`} className="back-link">
        <ArrowLeft size={14} /> {project ? project.name : 'К проекту'}
      </Link>
      {body}
    </div>
  )
}
